import React, { useState, useEffect, Fragment } from "react";

import { t } from "i18next";
import Select from "react-select";

import BaseActionButton from "../Buttons/BaseActionButton";
import { isEmpty } from "lodash";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import ValidationError from "../Shared/ValidationError";
import LanguagesAPI from "../../API/languages.api";

const LocalizedLablesCard = ({
    localizations,
    setLocalizations,
    apiLanguages,
    setApiLanguages,
    mainLanguageErrorMessage = "",
    label = "label",
    primaryButtonColor = "",
    secondActionButton = {},
    isFieldDisabled = false,
}) => {
    const [showAdded, setShowAdded] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [languageFlagData, setLanguageFlagData] = useState({});

    const [localizedLabel, setLocalizedLabel] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState("");

    const [selectOption, setSelectOption] = useState([]);

    useEffect(() => {
        getLanguages();
    }, []);

    useEffect(() => {
        setSelectOption(getLanguageOptions());
    }, [localizations, languages]);

    useEffect(() => {
        mapLanguages(apiLanguages);
    }, [apiLanguages]);

    const getLanguages = async () => {
        if (isEmpty(apiLanguages)) {
            const result = await LanguagesAPI.getLanguages();

            setApiLanguages(result);
        }
    };

    const mapLanguages = (result) => {
        const mappedLanguages = [];
        const mappedFlags = {};
        result?.data?.forEach((language) => {
            mappedLanguages.push(language.id);
            mappedFlags[language.id] = {
                flag: language.languageflag,
                language: language.displayName,
            };
        });

        setLanguages(mappedLanguages);
        setLanguageFlagData(mappedFlags);
    };

    const removeAddedLabel = () => {
        setShowAdded(false);
        setLocalizedLabel("");
        setSelectedLanguage("");
    };

    const confirmAddedLabel = () => {
        if (!selectedLanguage.value || !localizedLabel) return;

        const newLocalization = { ...localizations };
        newLocalization[selectedLanguage.value] = localizedLabel;
        setLocalizations(newLocalization);

        setShowAdded(false);
        setLocalizedLabel("");
        setSelectedLanguage("");
    };

    const handleRemoveLabel = (key) => {
        const newLocalization = { ...localizations };
        delete newLocalization[key];
        setLocalizations(newLocalization);
    };

    const handleSelectChange = (value) => {
        setSelectedLanguage(value);
    };

    const handleInputChange = (e) => {
        setLocalizedLabel(e.target.value);
    };

    const handleLocalizationChange = (key, value) => {
        const newLocalization = { ...localizations };
        newLocalization[key] = value;
        setLocalizations(newLocalization);
    };

    const getLanguageOptions = () => {
        const languageOptions = [];

        languages.forEach((option, index) => {
            if (!localizations[option] && localizations[option] !== "")
                languageOptions.push({
                    value: option,
                    searchable: languageFlagData[option]?.language,
                    label: (
                        <div
                            className="flex items-center text-xs mr-2"
                            key={index}
                        >
                            <div
                                className={`flag-icon flag-icon-${languageFlagData[option]?.flag}`}
                            ></div>
                            <h3 className="uppercase text-center ml-1">
                                {languageFlagData[option]?.language}
                            </h3>
                        </div>
                    ),
                });
        });
        return languageOptions;
    };

    const renderAddingRow = () => {
        if (!showAdded) return;
        return (
            <div
                className={`flex items-center p-2 gap-1 ${
                    showAdded ? "" : "hide"
                }`}
            >
                <div className="flex items-center w-48">
                    <Select
                        className="basic-single w-full"
                        value={selectedLanguage}
                        options={selectOption}
                        placeholder={t("selectPlaceholder", {
                            entity: t("language"),
                        })}
                        theme={getReactSelectTheme}
                        styles={getReactSelectStyling({
                            height: 32,
                        })}
                        onChange={handleSelectChange}
                        getOptionValue={(value) => value.searchable}
                    />
                </div>
                <input
                    className="input-element"
                    name="label"
                    type="text"
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            confirmAddedLabel();
                        }
                    }}
                    onBlur={confirmAddedLabel}
                    value={localizedLabel}
                    placeholder={t("label")}
                />
                <BaseActionButton
                    icon="fas fa-check"
                    className="table-btn table-btn-blue"
                    onClick={confirmAddedLabel}
                    disabled={!localizedLabel || !selectedLanguage}
                />
                <BaseActionButton
                    icon="fas fa-trash-alt"
                    className="table-btn table-btn-orange m-0"
                    onClick={removeAddedLabel}
                />
            </div>
        );
    };

    const getRowData = (key, value, index) => {
        const isMainLanguage = key === "en";
        return (
            <Fragment key={`localization-node-${key}-${index}`}>
                <div className="flex items-center p-2 gap-1" key={index}>
                    <div className="flex items-center text-xs min-w-22 gap-1">
                        <span
                            className={`flag-icon flag-icon-${languageFlagData[key]?.flag}`}
                        ></span>
                        <h3 className="uppercase text-center color-blue">
                            {languageFlagData[key]?.language}
                        </h3>
                    </div>
                    <input
                        className={`input-element ${
                            isFieldDisabled ? "disabled" : ""
                        }`}
                        name="label"
                        type="text"
                        onChange={(e) =>
                            handleLocalizationChange(key, e.target.value)
                        }
                        value={value ?? ""}
                        placeholder={t("label")}
                        disabled={isFieldDisabled}
                    />

                    {!isMainLanguage && (
                        <BaseActionButton
                            icon="fas fa-trash-alt"
                            className="table-btn table-btn-orange m-0"
                            onClick={() => handleRemoveLabel(key)}
                        />
                    )}
                </div>
                {isMainLanguage && (
                    <ValidationError errorMessage={mainLanguageErrorMessage} />
                )}
            </Fragment>
        );
    };

    const renderLabels = () => {
        let mainLanguageNode;
        let localizationNodes = [];

        Object.entries(localizations)?.forEach(([key, value], index) => {
            const isMainLanguage = key === "en";
            const node = getRowData(key, value, index);

            if (isMainLanguage) {
                mainLanguageNode = node;
            } else {
                localizationNodes.push(node);
            }
        });

        return [mainLanguageNode, ...localizationNodes];
    };

    return (
        <>
            <div className="input-group flex flex-col">
                <div className="tags whitespace-wrap">
                    <h4 className="uppercase text-xs">{t(`${label}`)}</h4>
                </div>
                {renderLabels()}
                {renderAddingRow()}
                {!isEmpty(selectOption) && (
                    <div className="flex justify-end">
                        {!isEmpty(secondActionButton) && (
                            <div className="self-end p-2">
                                <span
                                    onClick={secondActionButton.handler}
                                    className={`hyper-btn text-xs ${
                                        secondActionButton.disabled
                                            ? "disabled"
                                            : ""
                                    }`}
                                >
                                    {secondActionButton.label}
                                </span>
                            </div>
                        )}
                        <div className="self-end p-2">
                            <span
                                onClick={() => setShowAdded(true)}
                                className={`cursor-pointer ${
                                    primaryButtonColor || "hyper-btn"
                                } text-xs ${
                                    !localizations.en || showAdded
                                        ? "disabled"
                                        : ""
                                }`}
                            >
                                <i className="fas fa-plus fa-xs mr-1" />
                                {t("addTitle", { entity: t("localization") })}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default LocalizedLablesCard;
